/**
 * A picker's mobile presentation: a bottom sheet (BON-534).
 *
 * The dropdown was anchored to a trigger near the left edge and ran off the
 * right of a 390px screen, clipping the director and the region count. The
 * design's answer is this: full width, pinned to the bottom, its own header, a
 * scrim over the page, and rows sized for a thumb.
 *
 * FIXED to the viewport, not absolute inside the page. The design positions it
 * inside its own device frame, which the prototype provides; the real app has
 * scrolling ancestors and `overflow: hidden` between here and the body, any of
 * which would clip or mis-place an absolutely positioned sheet. It renders
 * through a portal for the same reason.
 *
 * The portal lands on `document.body`, which is OUTSIDE `.nb-dashboards` —
 * where every `--nb-*` token and every dashboards element style is scoped. So
 * the sheet carries its own `.nb-dashboards` island, as the bookings pager
 * does. Without it the sheet renders with no background at all and its rows
 * sit transparently over the page; `display: contents` keeps the island from
 * adding a box of its own, and `html.dark .nb-dashboards` still selects it, so
 * dark mode needs nothing extra.
 */
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { ScopeSheetHeader } from './ScopeSheetHeader';
import { useBodyScrollLock } from './useBodyScrollLock';
import { useSheetDrag } from './useSheetDrag';

export interface ScopeSheetProps {
  /** What the sheet is picking — its heading, and the dialog's name. */
  title: string;
  onClose: () => void;
  /** The rows. The caller's, so regions and app modes share this chrome. */
  children: ReactNode;
}

export function ScopeSheet({ title, onClose, children }: ScopeSheetProps) {
  const drag = useSheetDrag(onClose);

  // The page behind must not scroll under the finger.
  useBodyScrollLock(true);

  // A sheet covering the page has to close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="nb-dashboards" style={{ display: 'contents' }}>
      <div
        data-testid="scope-scrim"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 59,
          background: 'rgba(20,28,18,.42)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 60,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '78%',
          background: 'var(--nb-bg)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          boxShadow: '0 -12px 40px -8px rgba(0,0,0,.35)',
          transform: drag.offset ? `translateY(${drag.offset}px)` : undefined,
          transition: drag.dragging ? undefined : 'transform .2s ease-out',
        }}
      >
        {/* Handle + header: the only draggable strip, and the only part that
            does not scroll. */}
        <ScopeSheetHeader title={title} onClose={onClose} drag={drag} />

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            // The home indicator sits over the bottom edge on a modern phone.
            padding: '8px 8px calc(14px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
